import { AxiosResponse } from 'axios';
import moment from 'moment';
import { ListPayload, SuccessResponse } from 'core/http/response';
import config from 'config';
import {
    AddRemoveJobFromManifestPayload,
    CreateDocumentResponse,
    CreateManifestPayload,
    CreateManifestResponse,
    DeleteDocumentResponse,
    GetDocumentPresignedUrlData,
    GetDocumentPresignedUrlResponse,
    GetDocumentUploadUrlParams,
    ManifestResponse,
    GetManifestHistoryParams,
    GetManifestHistoryResponse,
    GetManifestsParams,
    GetManifestsResponse,
    GetNotesForManifestParams,
    GetNotesForManifestResponse,
    ListDocumentsParams,
    ListDocumentsResponse,
    Manifest,
    ManifestDocument,
    ManifestHistory,
    ManifestNote,
    UpdateManifestPayload,
    UpdateManifestResponse,
    CreateUpdateManifestNoteResponse,
    UpdateManifestExternalReferencesPayload,
    ManifestDeclarationsResponse,
    CreateThirdPartyJobPayload,
    ThirdPartyJobResponse,
    CreateIcs2EnsDeclarationsParams,
    Ics2EnsDeclarationResponse,
    ListManifestEnsDeclarationsParams,
} from './types';
import axiosClient from '../../config/axios';
import { CreateDocumentPayload } from 'store/documents/document';
import { Ics2ListRow } from 'store/declarations/eu/ics2/ics2-declaration-list';
import { listIcs2Declarations } from 'store/declarations/client';

const baseUrl = config.declarationsUrl;

export const listManifests = async (params: GetManifestsParams): Promise<ListPayload<Manifest>> => {
    const response: AxiosResponse<GetManifestsResponse> = await axiosClient.get(`${config.declarationsUrl}/manifest`, {
        params,
    });
    return response.data.payload;
};

export const getManifestById = async (manifestId: string): Promise<Manifest> => {
    const response: AxiosResponse<ManifestResponse> = await axiosClient.get(`${baseUrl}/manifest/${manifestId}`);
    return response.data.payload;
};

export const createManifest = async (payload: CreateManifestPayload): Promise<Manifest> => {
    const response: AxiosResponse<CreateManifestResponse> = await axiosClient.post(`${baseUrl}/manifest`, payload);
    return response.data.payload;
};

export const updateManifest = async (manifestId: string, payload: UpdateManifestPayload): Promise<Manifest> => {
    const response: AxiosResponse<UpdateManifestResponse> = await axiosClient.put(
        `${baseUrl}/manifest/${manifestId}`,
        payload
    );
    return response.data.payload;
};

export const archiveManifest = async (manifestId: string): Promise<Manifest> => {
    const response: AxiosResponse<UpdateManifestResponse> = await axiosClient.put(
        `${baseUrl}/manifest/archive/${manifestId}`
    );
    return response.data.payload;
};

export const unarchiveManifest = async (manifestId: string): Promise<Manifest> => {
    const response: AxiosResponse<UpdateManifestResponse> = await axiosClient.put(
        `${baseUrl}/manifest/unarchive/${manifestId}`
    );
    return response.data.payload;
};

export const addJobToManifest = async ({ manifestId, jobId }: AddRemoveJobFromManifestPayload): Promise<Manifest> => {
    const response: AxiosResponse<UpdateManifestResponse> = await axiosClient.put(
        `${baseUrl}/manifest/${manifestId}/add/${jobId}`
    );
    return response.data.payload;
};

export const removeJobFromManifest = async ({
    manifestId,
    jobId,
}: AddRemoveJobFromManifestPayload): Promise<Manifest> => {
    const response: AxiosResponse<UpdateManifestResponse> = await axiosClient.put(
        `${baseUrl}/manifest/${manifestId}/remove/${jobId}`
    );
    return response.data.payload;
};

export const updateManifestNote = async (
    manifestId: string,
    noteId: string,
    payload: { title: string; note: string }
): Promise<ManifestNote> => {
    const response: AxiosResponse<CreateUpdateManifestNoteResponse> = await axiosClient.put(
        `${baseUrl}/manifest/${manifestId}/notes/${noteId}`,
        payload
    );

    return response.data.payload;
};

export const deleteManifestNote = async (manifestId: string, noteId: string): Promise<{}> => {
    const response: AxiosResponse<SuccessResponse<{}>> = await axiosClient.delete(
        `${baseUrl}/manifest/${manifestId}/notes/${noteId}`
    );
    return response.data.payload;
};

export const addNoteToManifest = async (
    manifestId: string,
    payload: {
        title: string;
        note: string;
    }
): Promise<ManifestNote> => {
    const response: AxiosResponse<CreateUpdateManifestNoteResponse> = await axiosClient.post(
        `${baseUrl}/manifest/${manifestId}/notes`,
        payload
    );
    return response.data.payload;
};

export const getNotesForManifest = async (
    manifestId: string,
    params?: GetNotesForManifestParams
): Promise<ListPayload<ManifestNote>> => {
    const response: AxiosResponse<GetNotesForManifestResponse> = await axiosClient.get(
        `${baseUrl}/manifest/${manifestId}/notes`,
        {
            params,
        }
    );
    return response.data.payload;
};

export const getHistoryByManifestId = async (
    manifestId: string,
    params: GetManifestHistoryParams
): Promise<ListPayload<ManifestHistory>> => {
    const response: AxiosResponse<GetManifestHistoryResponse> = await axiosClient.get(
        `${baseUrl}/manifest/${manifestId}/history`,
        { params }
    );
    return response.data.payload;
};

export const createDocument = async (manifestId: string, payload: CreateDocumentPayload): Promise<ManifestDocument> => {
    const response: AxiosResponse<CreateDocumentResponse> = await axiosClient.post(
        `${baseUrl}/manifest/documents/${manifestId}`,
        payload
    );
    return response.data.payload;
};

export const deleteDocument = async (manifestId: string, documentId: string): Promise<DeleteDocumentResponse> => {
    const response: AxiosResponse<any> = await axiosClient.delete(
        `${baseUrl}/manifest/documents/${manifestId}/delete/${documentId}`
    );
    return response.data;
};

export const listDocumentsByManifestId = async (
    manifestId: string,
    params: ListDocumentsParams
): Promise<ListDocumentsResponse> => {
    const response: AxiosResponse<ListDocumentsResponse> = await axiosClient.post(
        `${baseUrl}/manifest/documents/list/${manifestId}`,
        {},
        { params }
    );
    return response.data;
};

export const getDocumentUploadUrl = async (
    manifestId: string,
    params: GetDocumentUploadUrlParams
): Promise<GetDocumentPresignedUrlData> => {
    const response: AxiosResponse<GetDocumentPresignedUrlResponse> = await axiosClient.get(
        `${baseUrl}/manifest/documents/upload/${manifestId}`,
        { params }
    );
    return response.data.payload;
};

export const getDocumentDownloadUrl = async (
    manifestId: string,
    documentId: string
): Promise<GetDocumentPresignedUrlData> => {
    const response: AxiosResponse<GetDocumentPresignedUrlResponse> = await axiosClient.get(
        `${baseUrl}/manifest/documents/${manifestId}/download/${documentId}`
    );

    return response.data.payload;
};

export const assignManifestToOwner = async (manifestId: string, individualId: string): Promise<Manifest> => {
    const response: AxiosResponse<ManifestResponse> = await axiosClient.patch(
        `${baseUrl}/manifest/${manifestId}/assign-owner/${individualId}`
    );
    return response.data.payload;
};

export const updateManifestExternalReferences = async (
    manifestId: string,
    body: UpdateManifestExternalReferencesPayload
): Promise<Manifest> => {
    const response: AxiosResponse<ManifestResponse> = await axiosClient.patch(
        `${baseUrl}/manifest/${manifestId}/external-references`,
        body
    );
    return response.data.payload;
};

// 3rd-party jobs
export const addThirdPartyJob = async (
    manifestId: string,
    payload: CreateThirdPartyJobPayload
): Promise<ThirdPartyJobResponse> => {
    const response: AxiosResponse<SuccessResponse<any>> = await axiosClient.post(
        `${baseUrl}/manifest/${manifestId}/third-party-jobs`,
        payload
    );
    return response.data.payload;
};

export const listThirdPartyJobs = async (manifestId: string): Promise<ThirdPartyJobResponse[]> => {
    const response: AxiosResponse<SuccessResponse<ThirdPartyJobResponse[]>> = await axiosClient.get(
        `${baseUrl}/manifest/${manifestId}/third-party-jobs`
    );
    return response.data.payload;
};

export const updateThirdPartyJob = async (
    manifestId: string,
    thirdPartyJobId: string,
    payload: CreateThirdPartyJobPayload
): Promise<ThirdPartyJobResponse> => {
    const response: AxiosResponse<SuccessResponse<ThirdPartyJobResponse>> = await axiosClient.put(
        `${baseUrl}/manifest/${manifestId}/third-party-jobs/${thirdPartyJobId}`,
        payload
    );
    return response.data.payload;
};

export const deleteThirdPartyJob = async (
    manifestId: string,
    thirdPartyJobId: string
): Promise<ThirdPartyJobResponse> => {
    const response: AxiosResponse<SuccessResponse<ThirdPartyJobResponse>> = await axiosClient.delete(
        `${baseUrl}/manifest/${manifestId}/third-party-jobs/${thirdPartyJobId}`
    );
    return response.data.payload;
};

export const getManifestDeclarations = async (manifestId: string): Promise<ManifestDeclarationsResponse[]> => {
    const response: AxiosResponse<SuccessResponse<ManifestDeclarationsResponse[]>> = await axiosClient.get(
        `${baseUrl}/manifest/${manifestId}/declarations`
    );
    return response.data.payload;
};

export const getManifestJobsWithTransportDetailsDiscrepancies = async (
    manifestId: string
): Promise<ManifestDeclarationsResponse[]> => {
    const response: AxiosResponse<SuccessResponse<ManifestDeclarationsResponse[]>> = await axiosClient.get(
        `${baseUrl}/manifest/${manifestId}/affected-jobs`
    );
    return response.data.payload;
};

export const createIcs2EnsDeclarations = async (
    manifestId: string,
    params: CreateIcs2EnsDeclarationsParams
): Promise<Ics2EnsDeclarationResponse[]> => {
    const response: AxiosResponse<SuccessResponse<Ics2EnsDeclarationResponse[]>> = await axiosClient.post(
        `${baseUrl}/manifest/${manifestId}/ics2-ens`,
        {},
        { params }
    );
    return response.data.payload;
};

const ENS_PAGE_SIZE = 10;

export const listManifestEnsDeclarations = async (
    manifestId: string,
    params?: ListManifestEnsDeclarationsParams
): Promise<ListPayload<Ics2ListRow>> => {
    const exactDate = params?.exactDate ? moment(params.exactDate, 'YYYY-MM-DD') : undefined;

    return listIcs2Declarations({
        page: params?.page ?? 0,
        size: params?.size ?? ENS_PAGE_SIZE,
        sourceManifestId: manifestId,
        query: params?.query,
        archived: params?.archived,
        startDate: exactDate?.clone().startOf('day').toISOString(),
        endDate: exactDate?.clone().endOf('day').toISOString(),
    });
};
